import Dialog from '@material-ui/core/Dialog'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import CloseIcon from '@material-ui/icons/Close'
import { DistributorDataTypeV2 } from 'elements/hardwareSelectorV2/types'
import { IconButton, styled } from 'opensolar-ui'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { forwardRef, useEffect, useRef } from 'react'
import { useTranslate } from 'react-admin'
import { WarehouseLocatorMapContent } from './WarehouseLocator'

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(1),
  },
}))

const StyledDialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 16px 20px 24px',
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.typography.pxToRem(18),
}))

const StyledDialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(0),
}))

const StyledCloseIcon = styled(CloseIcon)(({ theme }) => ({
  color: theme.palette.grey[600],
  height: theme.spacing(2.5),
  width: theme.spacing(2.5),
}))

export interface WarehouseLocatorModalProps {
  data: DistributorDataTypeV2[]
  open: boolean
  onClose: () => void
  projectAddress?: string
  orgAddress?: string
  orgCountry?: string
  selectedDistributor?: string
  productCode?: string
  productName?: string
  amplitudeEventType?: string
}

export const WarehouseLocatorModal = forwardRef<HTMLDivElement, WarehouseLocatorModalProps>(
  ({ data, open, onClose, projectAddress, orgAddress, orgCountry = 'United Kingdom', selectedDistributor, productCode, productName, amplitudeEventType }, ref) => {
    const translate = useTranslate()

    const hasLoggedOpen = useRef(false)

    useEffect(() => {
      if (open) {
        if (!hasLoggedOpen.current) {
          hasLoggedOpen.current = true
          logAmplitudeEvent('hardware_product_warehouse_locator_opened', {
            action: 'view',
            source: 'warehouse_locator_modal',
            context: 'modal_opened',
            product_code: productCode,
            product_name: productName,
            distributor_key: selectedDistributor,
          }, amplitudeEventType)
        }

        try {
          window.editor?.signals?.typingInField?.dispatch(true)
          if (window.Designer) {
            window.Designer.typingInField = true
          }
        } catch (err) {}

        return () => {
          try {
            window.editor?.signals?.typingInField?.dispatch(false)
            if (window.Designer) {
              window.Designer.typingInField = false
            }
          } catch (err) {}
        }
      } else {
        hasLoggedOpen.current = false
      }
    }, [open, productCode, productName, selectedDistributor, amplitudeEventType])

    const handleKeyDown = (e: React.KeyboardEvent) => {
      e.stopPropagation()
    }

    return (
      <StyledDialog ref={ref} open={open} onClose={onClose} maxWidth={'lg'} fullWidth onKeyDown={handleKeyDown}>
        <StyledDialogTitle disableTypography>
          {translate('Stock available per location')}
          <IconButton onClick={onClose}>
            <StyledCloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <StyledDialogContent>
          <WarehouseLocatorMapContent
            data={data}
            projectAddress={projectAddress}
            orgAddress={orgAddress}
            orgCountry={orgCountry}
            selectedDistributor={selectedDistributor}
            open={open}
            amplitudeEventType={amplitudeEventType}
          />
        </StyledDialogContent>
      </StyledDialog>
    )
  }
)

export default WarehouseLocatorModal
